import React from "react";
import { Box, Grid, Typography, Container, Button, useTheme } from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import VerifiedIcon from "@mui/icons-material/Verified";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import PageTransition from "./PageTransition";

const AboutUs = () => {
    const theme = useTheme();
    return (
        <Box sx={{ backgroundColor: "#f9f9f9", py: 6 }}>
            <PageTransition>
                <Box sx={{ textAlign: "center", mb: 8 }}>
                    <Typography
                        variant="h3"
                        align="center"
                        sx={{
                            color: theme.palette.secondary.main,
                            fontWeight: 600,
                            fontSize: "2.5rem",
                            letterSpacing: '0.5px',
                            marginBottom: 3,
                        }}
                    >
                        Welcome to MC3 Smart Tech
                    </Typography>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        sx={{
                            maxWidth: 900,
                            mx: "auto",
                            fontWeight: 400,
                            lineHeight: 1.6,
                            fontSize: "1.1rem",
                        }}
                    >
                        At MC3 Smart Tech, we empower businesses to innovate and grow. By combining advanced technology with creative problem-solving, we help clients overcome their most complex challenges and drive digital transformation across industries.
                    </Typography>
                </Box>

                <Container>
                    <Grid container spacing={6} justifyContent="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <Box
                                sx={{
                                    backgroundColor: "#fff",
                                    p: 4,
                                    textAlign: "center",
                                    borderRadius: 4,
                                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                                    transition: "transform 0.3s",
                                    "&:hover": {
                                        transform: "translateY(-10px)",
                                    },
                                }}
                            >
                                <EmojiObjectsIcon sx={{ fontSize: 60, color: theme.palette.tertiary.main, mb: 2 }} />
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Innovation at Heart
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    We foster a culture of creativity, pushing boundaries to create solutions that keep your business ahead of the curve. Our innovative approach is all about blending technology with visionary thinking.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Box
                                sx={{
                                    backgroundColor: "#fff",
                                    p: 4,
                                    textAlign: "center",
                                    borderRadius: 4,
                                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                                    transition: "transform 0.3s",
                                    "&:hover": {
                                        transform: "translateY(-10px)",
                                    },
                                }}
                            >
                                <GroupWorkIcon sx={{ fontSize: 60, color: theme.palette.tertiary.main, mb: 2 }} />
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    True Collaboration
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    We believe that the best solutions come from working closely with our clients. Collaboration is key to creating strategies that align perfectly with your business goals.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Box
                                sx={{
                                    backgroundColor: "#fff",
                                    p: 4,
                                    textAlign: "center",
                                    borderRadius: 4,
                                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                                    transition: "transform 0.3s",
                                    "&:hover": {
                                        transform: "translateY(-10px)",
                                    },
                                }}
                            >
                                <VerifiedIcon sx={{ fontSize: 60, color: theme.palette.tertiary.main, mb: 2 }} />
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Excellence Every Step
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    We’re committed to excellence in everything we do. From the smallest detail to the grandest vision, we deliver results that exceed expectations and set new standards for quality and performance.
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Box
                                sx={{
                                    backgroundColor: "#fff",
                                    p: 4,
                                    textAlign: "center",
                                    borderRadius: 4,
                                    boxShadow: "0 8px 16px rgba(0, 0, 0, 0.1)",
                                    transition: "transform 0.3s",
                                    "&:hover": {
                                        transform: "translateY(-10px)",
                                    },
                                }}
                            >
                                <CodeIcon sx={{ fontSize: 60, color: theme.palette.tertiary.main, mb: 2 }} />
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    Cutting-Edge Expertise
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                                    Our team brings deep technical expertise and years of experience to every project. With a keen focus on emerging technologies, we create software that addresses the challenges of today and anticipates the needs of tomorrow.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>

                <Box sx={{ textAlign: "center", mt: 8 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.secondary.main,
                            marginBottom: 4,
                        }}
                    >
                        Why MC3 Smart Tech?
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.secondary"
                        sx={{
                            maxWidth: 1000,
                            mx: "auto",
                            mb: 6,
                            fontWeight: 300,
                            fontSize: "1rem",
                            lineHeight: 1.8,
                        }}
                    >
                        With a client-first mindset, we work as partners, ensuring we understand your unique goals. We leverage cutting-edge technology to deliver scalable, secure, and innovative solutions. Our collaborative process guarantees the best outcomes, tailored just for you.
                    </Typography>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.secondary.main,
                            fontWeight: "bold",
                            fontSize: "1.1rem",
                            padding: "10px 20px",
                            borderRadius: 2,
                            "&:hover": {
                                backgroundColor: theme.palette.primary.dark,
                            },
                        }}
                        href="/contact-us"
                    >
                        Get In Touch
                    </Button>
                </Box>
            </PageTransition>
        </Box>
    );
};

export default AboutUs;
